import type { Easing, Keyframe } from '../../domain/schemas';
import { ease } from '../timeline';
import { MAX_PATH_KEYS, MAX_TRAIL_POINTS, type MotionPath } from './spec';

/**
 * 2D motion paths (Phase 3C): straight lines and polylines (`linear`) or chains of cubic
 * Bézier segments (`bezier`, points = start, then two controls and an end per segment).
 * Paths are compiled into ordinary x / y (and optionally rotation) keyframes — there is no
 * separate runtime path engine.
 *
 * Sampling policy (bounded fidelity vs. key count):
 *  - a linear path with linear easing and no orientation is keyed exactly at its vertices
 *    (timed by arc length), so linear interpolation reproduces it with no error;
 *  - otherwise the path is parametrised by arc length (Bézier segments flattened into 32
 *    pieces each) and keyed every `max(2, ceil(frames / 60))` frames with linear keys:
 *    at most 60 keys per repetition. Easing is applied to the distance travelled, so it is
 *    honoured across the whole path rather than per segment.
 *  - repetitions loop when the path is closed (it ends where it starts); an open path goes
 *    back and forth instead, so repeating never jumps.
 */
export interface Point {
  x: number;
  y: number;
}

const BEZIER_PIECES = 32;

/** Absolute anchor positions of a path that starts at `start` (relative paths are offsets). */
export function absolutePoints(path: MotionPath, start: Point): Point[] {
  const pts = path.points.map(([x, y]) => (path.relative ? { x: start.x + x, y: start.y + y } : { x, y }));
  // Reversing the point list also swaps each Bézier segment's two controls, as required.
  return path.reverse ? pts.reverse() : pts;
}

function cubic(p0: Point, c1: Point, c2: Point, p1: Point, t: number): Point {
  const u = 1 - t;
  const a = u * u * u;
  const b = 3 * u * u * t;
  const c = 3 * u * t * t;
  const d = t * t * t;
  return { x: a * p0.x + b * c1.x + c * c2.x + d * p1.x, y: a * p0.y + b * c1.y + c * c2.y + d * p1.y };
}

/** The path flattened into a polyline (Bézier segments into 32 pieces each). */
export function flatten(kind: MotionPath['kind'], pts: readonly Point[]): Point[] {
  if (kind === 'linear') return [...pts];
  const out: Point[] = [pts[0]!];
  for (let i = 0; i + 3 < pts.length; i += 3) {
    for (let k = 1; k <= BEZIER_PIECES; k++) out.push(cubic(pts[i]!, pts[i + 1]!, pts[i + 2]!, pts[i + 3]!, k / BEZIER_PIECES));
  }
  return out;
}

/** Arc-length table of a polyline: cumulative length at each vertex. */
export function arcLengths(poly: readonly Point[]): number[] {
  const out = [0];
  for (let i = 1; i < poly.length; i++) out.push(out[i - 1]! + Math.hypot(poly[i]!.x - poly[i - 1]!.x, poly[i]!.y - poly[i - 1]!.y));
  return out;
}

/** Point and direction (degrees) at arc length `s` along a polyline. */
export function pointAt(poly: readonly Point[], lengths: readonly number[], s: number): Point & { angle: number } {
  const total = lengths[lengths.length - 1]!;
  const d = Math.min(total, Math.max(0, s));
  let i = 1;
  while (i < lengths.length - 1 && lengths[i]! < d) i++;
  // Skip zero-length pieces so the direction is always defined.
  let j = i;
  while (j < poly.length - 1 && lengths[j]! - lengths[j - 1]! === 0) j++;
  const a = poly[j - 1]!;
  const b = poly[j]!;
  const seg = lengths[j]! - lengths[j - 1]!;
  const t = seg > 0 ? (d - lengths[j - 1]!) / seg : 0;
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, angle: (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI };
}

export interface PathKeys {
  /** Anchor positions (scene px) per key. */
  keys: { frame: number; x: number; y: number; angle: number }[];
  /** True when keys are exact vertices (linear interpolation reproduces the path). */
  exact: boolean;
}

const r2 = (v: number) => Math.round(v * 100) / 100;

/**
 * Keys for following a path from frame `f0` to `f1` (inclusive), `repeat` times.
 * Angles are unwrapped (continuous) relative to the direction at the start.
 */
export function pathKeys(kind: MotionPath['kind'], pts: readonly Point[], f0: number, f1: number, repeat: number, easing: Easing, orient: boolean): PathKeys {
  const poly = flatten(kind, pts);
  const lengths = arcLengths(poly);
  const total = lengths[lengths.length - 1]!;
  const closed = Math.hypot(poly[0]!.x - poly[poly.length - 1]!.x, poly[0]!.y - poly[poly.length - 1]!.y) < 0.5;
  const exact = kind === 'linear' && easing === 'linear' && !orient;
  const span = f1 - f0;
  const keys: PathKeys['keys'] = [];
  const angle0 = pointAt(poly, lengths, 0).angle;
  let previous = 0;
  const unwrap = (a: number) => {
    let v = a - angle0;
    while (v - previous > 180) v -= 360;
    while (v - previous < -180) v += 360;
    previous = v;
    return v;
  };
  const push = (frame: number, s: number, backwards: boolean) => {
    if (keys.length && keys[keys.length - 1]!.frame >= frame) return;
    const p = pointAt(poly, lengths, backwards ? total - s : s);
    keys.push({ frame, x: p.x, y: p.y, angle: unwrap(backwards ? p.angle + 180 : p.angle) });
  };
  for (let r = 0; r < repeat; r++) {
    const a = f0 + Math.round((span * r) / repeat);
    const b = f0 + Math.round((span * (r + 1)) / repeat);
    const backwards = !closed && r % 2 === 1;
    if (b <= a) continue;
    if (exact) {
      const order = backwards ? [...lengths].reverse().map((l) => total - l) : lengths;
      for (const l of order) push(a + Math.round((total > 0 ? l / total : 1) * (b - a)), l, backwards);
    } else {
      const stride = Math.max(2, Math.ceil((b - a) / MAX_PATH_KEYS));
      for (let f = a; f < b; f += stride) push(f, ease(easing, (f - a) / (b - a)) * total, backwards);
      push(b, total, backwards);
    }
  }
  return { keys, exact };
}

/** Path as a bounded polyline for drawing on the canvas preview. */
export function previewPolyline(kind: MotionPath['kind'], pts: readonly Point[]): [number, number][] {
  const poly = flatten(kind, pts);
  const step = Math.max(1, Math.ceil(poly.length / MAX_TRAIL_POINTS));
  const out: [number, number][] = [];
  for (let i = 0; i < poly.length; i += step) out.push([r2(poly[i]!.x), r2(poly[i]!.y)]);
  const last = poly[poly.length - 1]!;
  if (out[out.length - 1]![0] !== r2(last.x) || out[out.length - 1]![1] !== r2(last.y)) out.push([r2(last.x), r2(last.y)]);
  return out;
}

export const toKey = (frame: number, value: number, easing: Easing = 'linear'): Keyframe => ({ frame, value, easing });
